import React, { useEffect } from "react";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommunicationModel from "../../models/communications/Communication";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EmailIcon from "@mui/icons-material/Email";
import TextsmsIcon from "@mui/icons-material/Textsms";
import PhoneIcon from "@mui/icons-material/Phone";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useState } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuList from "@mui/material/MenuList";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InfoButton from "../support/InfoButton";
import { extractNumbersFromString, sanitizeMinimumNotificationFrequency } from "../../helpers/DataUpdateHelper";
import InputAdornment from "@mui/material/InputAdornment";
import InteractionDeleteButton from "../interactions/ui/InteractionDeleteButton";
import MessageSettingsLayout from "./MessageSettingsLayout";
import MediumSummaryIndicators from "./controls/MediumSummaryIndicators";
import LocalizationSettings from "../../models/config/CommunicationSettings/LocalizationSettings";
import InteractionCopyButton from "../interactions/ui/InteractionCopyButton";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));
const CommunicationBody = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "#f2f4f7",
  flexDirection: "row",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const CommunicationDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export interface CommunicationProps {
  communication: CommunicationModel;
  handleUpdateCommunication: (field: string, id: number, val: any, medium:string | undefined, lang: string | undefined ) => void;
  handleDeleteCommunication: (communicationID: number) => void;
  editMode: boolean;
  prioritiesList: Array<string>;
  handleCopyCommunication: (communication: CommunicationModel) => void;
}

const Communication = (props: CommunicationProps) => {

  // props.communication =  CommunicationModel.cleanup(props.communication);
  // handle errors on submission - do later
  // const [fieldErrors, setFieldErrors] = useState({
  //   notification_strategy: false,
  //   notification_minimum_frequency: false,
  //   count: false,
  //   priority: false,
  //   // Add other fields as necessary
  // });
  // const [hasError, setHasError] = useState(false);

  // const validateField = (fieldName:any, value:any) => {
  //   let isError = false;
  //   // Perform validation logic...
  //   if (value.trim() === '') isError = true;
  
  //   setFieldErrors(prev => ({ ...prev, [fieldName]: isError }));
  //   return !isError;
  // };

  // useEffect(() => {
  //   const errors = Object.values(fieldErrors);
  //   setHasError(errors.some(error => error === true));
  // }, [fieldErrors]);

  let priorityColor = "success";
  if (
    props.communication.properties.priority === "gentle reminder" ||
    props.communication.properties.priority ===
    "first communication long term followup"
  ) {
    priorityColor = "rgb(237, 247, 237);";
  } else if (
    props.communication.properties.priority === "first communication important" ||
    props.communication.properties.priority === "first reminder important"
  ) {
    priorityColor = "rgb(255, 244, 229)";
  } else if (props.communication.properties.priority === "last chance") {
    priorityColor = "rgb(253, 237, 237)";
  }

  // -5% = 5 Percent Before End

  const startDay = extractNumbersFromString(props.communication.start)[0];
  const startUnit = props.communication.start.includes("%")
    ? "Percent"
    : "Days";
  const relativeStartUnit = props.communication.start.includes("-")
    ? "BeforeEnd"
    : "AfterStart";

  const endDay = extractNumbersFromString(props.communication.end)[0];
  const endUnit = props.communication.end.includes("%") ? "Percent" : "Days";
  const relativeEndUnit = props.communication.end.includes("-")
    ? "BeforeEnd"
    : "AfterStart";

  // dropdown controls
  let defaultExpanded = false
  if (props.communication.justAdded) {
    defaultExpanded = true;
  }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const buildDayMetric = (day: number, unit: string, relativeUnit: string) => {
    if (relativeUnit == "BeforeEnd") {
      relativeUnit = "-";
    } else if (relativeUnit == "AfterStart") {
      relativeUnit = "";
    }
    if (unit == "Days") {
      unit = "days";
    } else if (unit == "Percent") {
      unit = "%";
    }
    const dayString = "" + day; // convert number to string
    const returnDatestring = relativeUnit + dayString + unit;
    return returnDatestring;
  };

  // the last two fields, count and minimum_notification_frequency,
  // only display if a certain notification_strategy was used
  let showCount = false;
  let showFrequency = false;
  let showDays = false;
  let notification_strategy_text = '';

  if (
    props.communication.properties.notification_strategy ===
    "sendonceduringinterval"
  ) {
    showCount = false;
    showFrequency = false;
    showDays = false;
    notification_strategy_text = "Send Once";
  } else if (
    props.communication.properties.notification_strategy ===
    "sendmultipletimesperinterval"
  ) {
    showCount = true;
    showFrequency = true;
    showDays = true;
    notification_strategy_text = "Send " + props.communication.properties.count + " Times per " + (props.communication.properties.notification_minimum_frequency_unit || "day");

  } else if (
    props.communication.properties.notification_strategy ===
    "sendonaregularschedule"
  ) {
    showCount = false;
    showFrequency = true;
    showDays = true;
    notification_strategy_text = "Send Between " + startDay + " " + startUnit + " " + relativeStartUnit.replace("AfterStart", "After Start").replace("BeforeEnd", "Before End") + " and " + endDay + " " + endUnit + " " + relativeEndUnit.replace("BeforeEnd", "Before End").replace("AfterStart", "After Start");

  }

  let prioritySummaryText = ""
  let priorityTextColor = "black"

  switch (props.communication.properties.priority) {
    case "first communication important": {
      prioritySummaryText = "First Communication Is Important";
      break;
    }
    case "last chance": {
      prioritySummaryText = "Last Chance";
      priorityTextColor = "red"

      break;
    }
    case "first reminder important": {
      prioritySummaryText = "First Reminder Is Important";
      break;
    }
    case "first communication long term followup": {
      prioritySummaryText = "First Communication Long Term Followup";
      priorityTextColor = "green"
      break;
    }
    case "gentle reminder": {
      prioritySummaryText = "Gentle Reminder";
      priorityTextColor = "green"
      break;
    }
  }

  // handle medium indicators in this component and child components
  let emailIndicator = true;
  let smsIndicator = true;
  let appIndicator = true;

  if(LocalizationSettings.isEmpty(props.communication.properties.message.app?.en as LocalizationSettings) 
  && LocalizationSettings.isEmpty(props.communication.properties.message.app?.es as LocalizationSettings)){
    appIndicator = false; 
   }
 if(LocalizationSettings.isEmpty(props.communication.properties.message.email?.en as LocalizationSettings) 
  && LocalizationSettings.isEmpty(props.communication.properties.message.email?.es as LocalizationSettings)){
    emailIndicator = false;
 }
 if(LocalizationSettings.isEmpty(props.communication.properties.message.sms?.en as LocalizationSettings) 
  && LocalizationSettings.isEmpty(props.communication.properties.message.sms?.es as LocalizationSettings)){
    smsIndicator = false; 
 }

  return (
    <div style={{ marginBottom: "10px" }} data-testid="communication">
      <Accordion defaultExpanded={defaultExpanded} disabled={!props.editMode}>
        <CommunicationBody
          data-testid="CommunicationBody"
          sx={{ backgroundColor: priorityColor }}
          expandIcon={<ExpandMoreIcon sx={{}} />}
          aria-controls="panel2a-content"
          id="panel2a-header"
        >
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={0}>
              <Grid item xs={3}>
                {/* {mediumIcon} */}
                <Chip
                  style={{ marginLeft: "45px" }}
                  size="small"
                  label={props.communication.start.replace("days", " Days")}
                />
              </Grid>
              <Grid item xs={3}>
                {/* <Typography>
                  <div style={{ width: "180px", fontWeight: "bolder", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    { props.communication.properties.message.email?.en?.subject || props.communication.properties.message.sms?.en?.text || props.communication.properties.message.sms?.en?.body}
                  </div>
                </Typography> */}
                <MediumSummaryIndicators email={emailIndicator} sms={smsIndicator} app={appIndicator} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h7">
                  <div style={{ color: priorityTextColor, position: "absolute", top: 10, fontWeight: "bold", fontSize: "8pt", width: "250px", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                    {prioritySummaryText}
                  </div>
                  <div style={{ position: "absolute", top: 25, fontStyle: "italic", fontSize: "8pt", width: "300px", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                    {notification_strategy_text}
                  </div>

                </Typography>
                {/* additional options dropdown */}
                <IconButton
                  data-testid="communicationSettingsDropdownButton"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 50

                  }}
                  id="basic-button"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                >
                  <MenuList style={{ color: "red" }}>
                    {/* delete communications */}
                    <MenuItem
                      data-testid="deleteCommunicationButton"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation(); // this doesnt stop accordion from expanding for some reason
                        handleClose();
                        props.handleDeleteCommunication(props.communication.id);
                      }}
                    >
                      <ListItemIcon>
                        <DeleteForeverIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Delete</ListItemText>
                    </MenuItem>
                  </MenuList>
                </Menu>
                <span style={{
                  position: "absolute",
                  top: 8,
                  right: 50

                }} >

                  <InteractionCopyButton type = "communication"  communication={props.communication} handleCopyCommunication={props.handleCopyCommunication}/>
                  <InteractionDeleteButton communicationID={props.communication.id} handler={(e: React.MouseEvent<HTMLElement>) => {
                    // e.stopPropagation(); // this doesnt stop accordion from expanding for some reason
                    handleClose();
                    props.handleDeleteCommunication(props.communication.id);
                  }} /> </span>


                {/* priority indicator */}
                {/* {props.communication.properties.priority === "lastchance" && (
                  <div                   style={{
                    position:"absolute",
                    top: 15,
                    right: 80

                  }}>                  <Tooltip title="This is a HIGH PRIORITY communication!" >
                  <ReportProblemOutlinedIcon
                    data-testid="highPriorityCommunicationIndicator"
                    style={{ float: "right", marginRight: "20px" }}
                    color="error"
                  />
                </Tooltip></div>

                )} */}
              </Grid>
            </Grid>
          </Box>
        </CommunicationBody>

        <CommunicationDetails>
          <div style={{ padding: "10px", display: "flex" }}>
            <div
              style={{
                width: "99%",
                padding: "20px",
                borderRadius: "5px 5px",
                border: "1pt solid lightgray",
              }}
            >
              {/* NOTIFICATION STRATEGY */}
              <FormControl sx={{ width: "99%" }} error={!props.communication.properties.notification_strategy}>
                <FormLabel id="demo-controlled-radio-buttons-group">
                  Notification Strategy
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={props.communication.properties.notification_strategy}
                  defaultValue={
                    props.communication.properties.notification_strategy
                  }
                  // onChange={handleNotificationStrategyChange}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (props.handleUpdateCommunication) {
                      props.handleUpdateCommunication(
                        "notification_strategy",
                        props.communication.id,
                        event.target.value,
                        undefined,
                        undefined
                      );
                    }

                    
                      // if (props.handleUpdateCommunication) {
                      //   props.handleUpdateCommunication(
                      //     "notification_minimum_frequency_unit",
                      //     props.communication.id,
                      //     event.target.value,
                      //   );
                      // }
                      // const notificationMinimumFrequencyString = buildMinimumNotificationFrequencyMetric(
                      //   props.communication.properties.notification_minimum_frequency,
                      //   event.target.value 
                      // )
                      // if (props.handleUpdateCommunication) {
                      //   props.handleUpdateCommunication(
                      //     "notification_minimum_frequency",
                      //     props.communication.id,
                      //     notificationMinimumFrequencyString,
                      //   );
                      // }
                    }
                  }
                 
                >
                  <FormControlLabel
                    value="sendonceduringinterval"
                    control={<Radio  />}
                    label={
                      <span>
                        send once during interval{" "}
                        <span style={{ position: "absolute", right: 0 }}>
                          <InfoButton
                            float={true}
                            flavor="info"
                            type={"SendOnceDuringInterval"}
                          />
                        </span>
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="sendmultipletimesperinterval"
                    control={<Radio />}
                    label={
                      <span>
                        send multiple times per interval{" "}
                        <span style={{ position: "absolute", right: 0 }}>
                          <InfoButton
                            float={true}
                            flavor="info"
                            type={"SendMultipleTimesPerInterval"}
                          />
                        </span>
                      </span>
                    }
                  />
                  <FormControlLabel
                    value="sendonaregularschedule"
                    control={<Radio />}
                    label={
                      <span>
                        send on a regular schedule{" "}
                        <span style={{ position: "absolute", right: 0 }}>
                          <InfoButton
                            float={true}
                            flavor="info"
                            type={"SendOnARegularSchedule"}
                          />
                        </span>
                      </span>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
          <div style={{ padding: "10px", display: "flex" }}>
            {/* MINIMUM FREQ UNIT*/}
            {showFrequency && (<FormControl>
              <InputLabel id="simple-select-label-priority">
                Unit
              </InputLabel>
              <Select
              style={{minWidth:"102px"}}
              error={!props.communication.properties.notification_minimum_frequency_unit}
                labelId="simple-select-label-priority"
                id="demo-simple-select"
                value={props.communication.properties.notification_minimum_frequency_unit || ""}
                label="Start Unit"
                onChange={(event: SelectChangeEvent) => {
                  if (props.handleUpdateCommunication) {
                    props.handleUpdateCommunication(
                      "notification_minimum_frequency_unit",
                      props.communication.id,
                      event.target.value,
                      undefined,
                      undefined
                    );
                  }
                }
              }
              >
                <MenuItem value={"Days"}>Days</MenuItem>
                <MenuItem value={"Percent"}>Percent</MenuItem>
              </Select>
            </FormControl>)}
            {/* MINIMUM FREQ */}
            {showFrequency && (
              <TextField 
                error={!props.communication.properties.notification_minimum_frequency} 
                sx={{ minWidth: "250px", marginRight: "10px" }}
                id="outlined-required"
                label="Minimum Notification Frequency"
                defaultValue={
                  sanitizeMinimumNotificationFrequency('' + props.communication.properties.notification_minimum_frequency)
                }
                // InputProps={{
                //   startAdornment: (
                //     <InputAdornment position="end">Days: &nbsp;</InputAdornment>
                //   ),
                // }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (props.handleUpdateCommunication) {
                    props.handleUpdateCommunication(
                      "notification_minimum_frequency",
                      props.communication.id,
                      event.target.value,
                      undefined,
                      undefined
                    );
                  }
                }}
              />
            )}
            {/* COUNT */}
            {showCount && (
              <TextField
                error={!props.communication.properties.count} 
                sx={{ width: "48%", marginLeft: "20px" }}
                id="outlined-required"
                label="Count"
                defaultValue={props.communication.properties.count}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (props.handleUpdateCommunication) {
                    props.handleUpdateCommunication(
                      "count",
                      props.communication.id,
                      event.target.value,
                      undefined,
                      undefined
                    );
                  }
                }}
              />
            )}
          </div>
          {showDays && <div style={{ width: "100%", margin: "auto", padding: "10px" }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                {/* START DAY*/}

                <TextField
                  sx={{ width: "80px" }}
                  id="outlined-required"
                  label="Start"
                  defaultValue={startDay}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (props.handleUpdateCommunication) {
                      const startDayString = buildDayMetric(
                        parseInt(event.target.value),
                        startUnit,
                        relativeStartUnit,
                      );
                      props.handleUpdateCommunication(
                        "start",
                        props.communication.id,
                        startDayString,
                        undefined,
                        undefined
                      );
                    }
                  }}
                />
                {/* START UNIT*/}
                <FormControl>
                  <InputLabel id="simple-select-label-priority">
                    Start Unit
                  </InputLabel>
                  <Select
                    labelId="simple-select-label-priority"
                    id="demo-simple-select"
                    value={startUnit}
                    label="Start Unit"
                    onChange={(event: SelectChangeEvent) => {
                      if (props.handleUpdateCommunication) {
                        const startDayString = buildDayMetric(
                          startDay,
                          event.target.value,
                          relativeStartUnit,
                        );
                        props.handleUpdateCommunication(
                          "start",
                          props.communication.id,
                          startDayString,
                          undefined,
                          undefined
                        );
                      }
                    }}
                  >
                    <MenuItem value={"Days"}>Days</MenuItem>
                    <MenuItem value={"Percent"}>Percent</MenuItem>
                  </Select>
                </FormControl>
                <FormControl style={{ width: "50%" }}>
                  <InputLabel id="simple-select-label-priority">
                    Relative Unit
                  </InputLabel>

                  <Select
                    labelId="simple-select-label-priority"
                    id="demo-simple-select"
                    value={relativeStartUnit}
                    defaultValue={relativeStartUnit}
                    label="Relative Unit"
                    onChange={(event: SelectChangeEvent) => {
                      if (props.handleUpdateCommunication) {
                        const startDayString = buildDayMetric(
                          startDay,
                          startUnit,
                          event.target.value,
                        );
                        props.handleUpdateCommunication(
                          "start",
                          props.communication.id,
                          startDayString,
                          undefined,
                          undefined
                        );
                      }
                    }}
                  >
                    <MenuItem value={"AfterStart"}>After Start</MenuItem>
                    <MenuItem value={"BeforeEnd"}>Before End</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {/* END DATE */}
                <TextField
                  sx={{ width: "80px" }}
                  id="outlined-required"
                  label="End"
                  defaultValue={endDay}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (props.handleUpdateCommunication) {
                      const endDayString = buildDayMetric(
                        parseInt(event.target.value),
                        endUnit,
                        relativeEndUnit,
                      );
                      props.handleUpdateCommunication(
                        "end",
                        props.communication.id,
                        endDayString,
                        undefined,
                        undefined
                      );
                    }
                  }}
                />
                <FormControl>
                  <InputLabel id="simple-select-label-priority">
                    Start Unit
                  </InputLabel>

                  <Select
                    labelId="simple-select-label-priority"
                    id="demo-simple-select"
                    value={endUnit}
                    label="Start Unit"
                    onChange={(event: SelectChangeEvent) => {
                      if (props.handleUpdateCommunication) {
                        const endDayString = buildDayMetric(
                          endDay,
                          event.target.value,
                          relativeEndUnit,
                        );
                        props.handleUpdateCommunication(
                          "end",
                          props.communication.id,
                          endDayString,
                          undefined,
                          undefined
                        );
                      }
                    }}
                  >
                    <MenuItem value={"Days"}>Days</MenuItem>
                    <MenuItem value={"Percent"}>Percent</MenuItem>
                  </Select>
                </FormControl>
                <FormControl style={{ width: "50%" }}>
                  <InputLabel id="simple-select-label-priority">
                    Relative Unit
                  </InputLabel>
                  <Select
                    labelId="simple-select-label-priority"
                    id="demo-simple-select"
                    value={relativeEndUnit}
                    label="Relative Unit"
                    onChange={(event: SelectChangeEvent) => {
                      if (props.handleUpdateCommunication) {
                        const endDayString = buildDayMetric(
                          endDay,
                          endUnit,
                          event.target.value,
                        );
                        props.handleUpdateCommunication(
                          "end",
                          props.communication.id,
                          endDayString,
                          undefined,
                          undefined
                        );
                      }
                    }}
                  >
                    <MenuItem value={"AfterStart"}>After Start</MenuItem>
                    <MenuItem value={"BeforeEnd"}>Before End</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>}

          {/* Message Communication Settings */}
          <MessageSettingsLayout 
            prioritiesList={props.prioritiesList}
            communication={props.communication}
            handleUpdateCommunication={props.handleUpdateCommunication}
            handleDeleteCommunication={props.handleDeleteCommunication}
            editMode={props.editMode} 
            emailIndicator={emailIndicator} 
            smsIndicator={smsIndicator} 
            appIndicator={appIndicator}            
          />
          <br/>

          {/* Div for Priority */}
          <div style={{ padding: "10px", display: "flex" }}>
            {/* PRIORITY */}
            <FormControl sx={{ width: "99%" }} error={!props.communication.properties.priority}>
              <InputLabel id="simple-select-label-priority">
                Priority
              </InputLabel>

              <Select
                error={!props.communication.properties.priority}
                labelId="simple-select-label-priority"
                id="demo-simple-select"
                value={props.communication.properties.priority}
                label="Priority"
                onChange={(event: SelectChangeEvent) => {
                  if (props.handleUpdateCommunication) {
                    props.handleUpdateCommunication(
                      "priority",
                      props.communication.id,
                      event.target.value as string,
                      undefined,
                      undefined
                    );
                  }
                }}
              >
                {props.prioritiesList.map((priority)=>{

                  return (
                    <MenuItem key = {priority.replace(/\s+/g, '')} value={priority}>
                    {priority}
                  </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </div>

          {/* <div><Button color={"error"}>Delete</Button></div> */}
        </CommunicationDetails>
      </Accordion>
    </div>
  );
};

export default Communication;
